import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import GoogleMap from './GoogleMap';
import { sendAppointmentEmail, validateAppointmentForm, formatAppointmentData } from '../services/emailService';
import { trackFormSubmission } from '../utils/analytics';
import './Contact.css';

const Contact = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous status
    setSubmitStatus({ type: '', message: '' });
    
    // Validate form data
    const validation = validateAppointmentForm(formData);
    if (!validation.isValid) {
      setSubmitStatus({
        type: 'error',
        message: validation.errors.join('. ')
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create WhatsApp message
      const whatsappMessage = createWhatsAppMessage(formData);
      
      // Open WhatsApp with pre-filled message
      window.open(whatsappMessage, '_blank');
      
      // Show success message
      setSubmitStatus({
        type: 'success',
        message: 'WhatsApp opened with your appointment details! Please send the message to complete your booking.'
      });
      
      // Track successful submission
      trackFormSubmission('whatsapp_booking_success');
      
      // Reset form after a short delay
      setTimeout(() => {
        setFormData({
          name: '',
          phone: '',
          service: '',
          message: ''
        });
      }, 2000);
      
      // Clear success message after 8 seconds
      setTimeout(() => {
        setSubmitStatus({ type: '', message: '' });
      }, 8000);
      
    } catch (error) {
      console.error('WhatsApp redirect error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Unable to open WhatsApp. Please call us directly at +977 981-3538101 or try again.'
      });
      
      // Track failed submission
      trackFormSubmission('whatsapp_booking_failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Create WhatsApp message with patient information
  const createWhatsAppMessage = (data) => {
    const clinicPhone = '9779813538101'; // Premier Dental Clinic WhatsApp number
    
    const message = `🦷 *APPOINTMENT REQUEST - Premier Dental Clinic*

👤 *Patient Name:* ${data.name}
📞 *Phone Number:* ${data.phone}
🏥 *Service Requested:* ${data.service}
📅 *Request Date:* ${new Date().toLocaleDateString('en-US', {
      timeZone: 'Asia/Kathmandu',
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })}

💬 *Additional Message:*
${data.message || 'No additional message'}

---
📍 *Clinic Location:* Ram Devi Complex, Suryabinayak, Bhaktapur
⏰ *Clinic Hours:* Daily 8:00 AM - 7:00 PM

Please confirm my appointment at your earliest convenience. Thank you!`;

    // Encode message for URL
    const encodedMessage = encodeURIComponent(message);
    
    // Create WhatsApp URL
    return `https://wa.me/${clinicPhone}?text=${encodedMessage}`;
  };

  const contactDetails = [
    {
      icon: '📍',
      title: 'Location',
      content: 'Ram Devi Complex, Suryabinayak, Bhaktapur'
    },
    {
      icon: '📞',
      title: 'Mobile',
      content: '+977 981-3538101',
      isPhone: true
    },
    {
      icon: '☎️',
      title: 'Landline',
      content: '015924538',
      isPhone: true
    },
    {
      icon: '🕒',
      title: 'Hours',
      content: 'Open Daily: 8:00 AM - 7:00 PM'
    },
    {
      icon: '🌟',
      title: 'Service Areas',
      content: 'Suryabinayak, Katunje, Bhaktapur & surrounding areas'
    }
  ];

  return (
    <section className="contact" id="contact" ref={ref}>
      <div className="container">
        <div className="contact-content">
          <motion.div 
            className="contact-info"
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2>Visit the Best Dental Clinic in Bhaktapur</h2>
            <div className="contact-details">
              {contactDetails.map((detail, index) => (
                <motion.div
                  key={index}
                  className="contact-item"
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <div className="contact-icon">{detail.icon}</div>
                  <div>
                    <h3>{detail.title}</h3>
                    <p>
                      {detail.isPhone ? (
                        <a href={`tel:${detail.title === 'Mobile' ? '+9779813538101' : '015924538'}`}>
                          {detail.content}
                        </a>
                      ) : (
                        detail.content
                      )}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
          
          <motion.div 
            className="contact-form"
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h3>Book Your Appointment</h3>
            <form onSubmit={handleSubmit}>
              {/* Status Message */}
              {submitStatus.message && (
                <motion.div 
                  className={`status-message ${submitStatus.type}`}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {submitStatus.type === 'success' ? '✅' : '❌'} {submitStatus.message}
                </motion.div>
              )}
              
              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-group">
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-group">
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                >
                  <option value="">Select Service</option>
                  <option value="consultation">General Consultation</option>
                  <option value="cleaning">Teeth Cleaning</option>
                  <option value="root-canal">Root Canal Treatment</option>
                  <option value="implants">Dental Implants</option>
                  <option value="braces">Braces/Orthodontics</option>
                  <option value="cosmetic">Cosmetic Dentistry</option>
                  <option value="restoration">Full-Mouth Restoration</option>
                </select>
              </div>
              <div className="form-group">
                <textarea
                  name="message"
                  placeholder="Additional Message (Optional)"
                  rows="4"
                  value={formData.message}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                ></textarea>
              </div>
              <motion.button 
                type="submit" 
                className={`btn btn-primary whatsapp-btn ${isSubmitting ? 'submitting' : ''}`}
                whileHover={!isSubmitting ? { scale: 1.05 } : {}}
                whileTap={!isSubmitting ? { scale: 0.95 } : {}}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Opening WhatsApp...
                  </>
                ) : (
                  <>
                    📱 Book via WhatsApp
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>
        </div>
        
        <GoogleMap />
      </div>
    </section>
  );
};

export default Contact;
